/* ============================================================
 * Kinetic Carving - Maker Directory: register form (wizard)
 * Built by Alpas Made.
 *
 * 3-step wizard with combobox multi-selects, file-list manager,
 * inline validation, char counter, phone auto-format, and async
 * submit. Pure vanilla. Safe to load on any page with one or
 * more <maker-register> elements.
 * ============================================================ */
(function () {
  if (window.__makerRegisterInit) return;
  window.__makerRegisterInit = true;

  var MAX_FILE_SIZE = 10 * 1024 * 1024;
  var MAX_IMAGE_DIM = 2048;

  /* Slugify a human-readable string (typically a company name) into a
     filename-safe token. v1.19.72: used at submit time to rename every
     uploaded logo and portfolio image so admins can identify a file on
     the Shopify CDN by its name (e.g. "acme-cnc-logo.jpg" instead of
     "IMG_1234.jpg"). Lowercase, ASCII letters / digits / hyphens only,
     no leading or trailing hyphens, capped at 60 chars to leave room
     for the suffix the renamer appends. */
  function slugifyForFilename(value) {
    if (!value) return '';
    var s = String(value).toLowerCase();
    s = s.replace(/[^a-z0-9]+/g, '-');
    s = s.replace(/^-+|-+$/g, '');
    if (s.length > 60) s = s.slice(0, 60).replace(/-+$/g, '');
    return s;
  }

  /* Wrap an existing File / Blob in a new File whose name follows the
     CDN convention. Mirror of the same helper in AdminDashboard.jsx and
     scripts/backfill-normalize-logos.js so a logo uploaded via any
     surface lands with a consistent shape on Shopify Files.

     Pattern:
       - logo:      {slug}-logo.{ext}
       - portfolio: {slug}-portfolio-{index}.{ext}     (index is 1-based)

     If slug is empty (company-name field unexpectedly blank), we fall
     back to "maker" so the filename is still distinguishable from a
     random source filename like "IMG_1234.jpg". */
  function renameUploadForCdn(file, slug, kind, index) {
    var base = slug || 'maker';
    var ext = '.jpg';
    if (file && file.type === 'image/png') ext = '.png';
    else if (file && file.type === 'image/webp') ext = '.webp';
    else if (file && file.type === 'image/gif') ext = '.gif';
    var name = kind === 'portfolio'
      ? base + '-portfolio-' + (index || 1) + ext
      : base + '-logo' + ext;
    try {
      return new File([file], name, { type: file.type, lastModified: Date.now() });
    } catch (e) {
      /* Older Safari fallback (same pattern preprocessImage and
         normalizeLogo use): mutate the existing Blob with a `name`
         shim so FormData treats it as a file with the new name. */
      try { file.name = name; } catch (_) {}
      return file;
    }
  }

  /* Re-encode the picked file through a Canvas before storing it on the
     form. Produces a clean sRGB image with no exotic ICC profiles, no
     EXIF rotations, no non-standard PNG chunks. Solves a class of
     "saved but Shopify image processor flipped to FAILED" bugs we hit
     with transparent PNG logos exported from design tools. Caps the
     longest edge at MAX_IMAGE_DIM so files always stay under Shopify's
     20 MP ceiling.

     GIFs pass through unchanged because Canvas re-encode strips
     animation frames. Anything that fails to decode (corrupt file,
     unsupported codec) bubbles up via the returned Promise so the
     caller can show a specific error to the maker. */
  function preprocessImage(file) {
    if (!file) return Promise.reject(new Error('No file'));
    if (file.type === 'image/gif') return Promise.resolve(file);
    return new Promise(function (resolve, reject) {
      var url;
      try { url = URL.createObjectURL(file); } catch (e) { return reject(e); }
      var img = new Image();
      img.onload = function () {
        try {
          var w = img.naturalWidth || img.width;
          var h = img.naturalHeight || img.height;
          if (!w || !h) { URL.revokeObjectURL(url); return reject(new Error('Image has zero dimensions.')); }
          var longest = Math.max(w, h);
          if (longest > MAX_IMAGE_DIM) {
            var r = MAX_IMAGE_DIM / longest;
            w = Math.max(1, Math.round(w * r));
            h = Math.max(1, Math.round(h * r));
          }
          var canvas = document.createElement('canvas');
          canvas.width = w; canvas.height = h;
          var ctx = canvas.getContext('2d');
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, w, h);
          URL.revokeObjectURL(url);
          var outType = file.type === 'image/jpeg' ? 'image/jpeg' : 'image/png';
          canvas.toBlob(function (blob) {
            if (!blob) return reject(new Error('Image encode failed.'));
            var ext = outType === 'image/png' ? '.png' : '.jpg';
            var baseName = (file.name || 'upload').replace(/\.[^.]+$/, '');
            try {
              var out = new File([blob], baseName + ext, { type: outType, lastModified: Date.now() });
              resolve(out);
            } catch (e) {
              /* Older Safari does not support new File(). Fall back to a
                 Blob that the FormData layer will treat as a file. */
              blob.name = baseName + ext;
              resolve(blob);
            }
          }, outType, 0.92);
        } catch (e) {
          URL.revokeObjectURL(url);
          reject(e);
        }
      };
      img.onerror = function () {
        URL.revokeObjectURL(url);
        reject(new Error('Could not read the image. Try saving as a flatter JPG or PNG.'));
      };
      img.src = url;
    });
  }

  /* v1.19.73: the upload-time normalization is split into two phases:
     `analyzeLogo` decodes the source file once, samples its pixels, and
     picks an auto background. `composeLogo` takes an analysis and a
     background color, paints the 512x512 output, and returns the final
     JPEG File. The live preview re-uses the same analysis across many
     background-swatch clicks (analyze once, compose many times). */

  /* Preset background swatches surfaced in the preview UI. The "auto"
     option is represented by passing `null` to `composeLogo`, which
     defers to analysis.autoBg. */
  var LOGO_BG_PRESETS = {
    light: '#fafaf9',
    dark: '#1c1917',
    neutral: '#737373'
  };

  /* Logo-specific analysis. Decodes the source file to an <img> via a
     local Blob URL (no CORS), samples 64x64, classifies non-transparent
     pixels by BT.709 luminance, and picks an auto background:
       - mostly dark content on transparent  -> off-white  (#fafaf9)
       - mostly light content on transparent -> near-black (#1c1917)
       - mixed content on transparent        -> midtone    (#737373)
       - opaque image                        -> average of four corners
     Returns an analysis object that holds a reference to the decoded
     Image so subsequent `composeLogo` calls can re-paint the 512x512
     output with a different background without re-decoding the file. */
  function analyzeLogo(file) {
    if (!file) return Promise.reject(new Error('No file'));
    if (file.type === 'image/gif') {
      /* GIFs pass through unchanged. Canvas re-encode would strip
         animation frames; the file-card preview will still render the
         first frame. Animated logos are very rare on a CNC / laser
         maker directory. The preview will show the GIF as-is. */
      return Promise.resolve({ gif: true, file: file, autoBg: null, classification: 'gif' });
    }
    return new Promise(function (resolve, reject) {
      var url;
      try { url = URL.createObjectURL(file); } catch (e) { return reject(e); }
      var img = new Image();
      img.onload = function () {
        try {
          var nw = img.naturalWidth || img.width;
          var nh = img.naturalHeight || img.height;
          if (!nw || !nh) { URL.revokeObjectURL(url); return reject(new Error('Image has zero dimensions.')); }

          var sampleSize = 64;
          var sample = document.createElement('canvas');
          sample.width = sampleSize; sample.height = sampleSize;
          var sctx = sample.getContext('2d');
          sctx.imageSmoothingEnabled = true;
          sctx.imageSmoothingQuality = 'high';
          sctx.drawImage(img, 0, 0, sampleSize, sampleSize);

          var pixelData;
          try {
            pixelData = sctx.getImageData(0, 0, sampleSize, sampleSize).data;
          } catch (e) {
            URL.revokeObjectURL(url);
            return reject(new Error('Could not analyze image pixels.'));
          }

          var hasAlpha = false;
          var dark = 0, light = 0, mid = 0;
          var solidCount = 0;
          for (var i = 0; i < pixelData.length; i += 4) {
            var r = pixelData[i], g = pixelData[i + 1], b = pixelData[i + 2], a = pixelData[i + 3];
            if (a < 250) { hasAlpha = true; continue; }
            solidCount++;
            var lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
            if (lum < 0.4) dark++;
            else if (lum > 0.6) light++;
            else mid++;
          }

          var autoBg, classification;
          if (hasAlpha && solidCount > 0) {
            var darkRatio = dark / solidCount;
            var lightRatio = light / solidCount;
            if (darkRatio >= 0.65) { autoBg = '#fafaf9'; classification = 'dark-on-transparent'; }
            else if (lightRatio >= 0.65) { autoBg = '#1c1917'; classification = 'light-on-transparent'; }
            else { autoBg = '#737373'; classification = 'mixed-on-transparent'; }
          } else if (hasAlpha && solidCount === 0) {
            autoBg = '#fafaf9'; classification = 'empty-transparent';
          } else {
            var corners = [
              [0, 0],
              [sampleSize - 1, 0],
              [0, sampleSize - 1],
              [sampleSize - 1, sampleSize - 1]
            ];
            var cr = 0, cg = 0, cb = 0;
            for (var ci = 0; ci < corners.length; ci++) {
              var cx = corners[ci][0], cy = corners[ci][1];
              var idx = (cy * sampleSize + cx) * 4;
              cr += pixelData[idx]; cg += pixelData[idx + 1]; cb += pixelData[idx + 2];
            }
            cr = Math.round(cr / 4); cg = Math.round(cg / 4); cb = Math.round(cb / 4);
            autoBg = 'rgb(' + cr + ',' + cg + ',' + cb + ')';
            classification = 'opaque';
          }

          /* It is safe to revoke the Blob URL here: the Image element
             has already decoded its bitmap into memory. Subsequent
             drawImage(img, ...) calls in composeLogo work fine without
             the URL alive. */
          URL.revokeObjectURL(url);

          resolve({
            gif: false,
            file: file,
            img: img,
            nw: nw,
            nh: nh,
            autoBg: autoBg,
            classification: classification,
            hasAlpha: hasAlpha
          });
        } catch (e) {
          URL.revokeObjectURL(url);
          reject(e);
        }
      };
      img.onerror = function () {
        URL.revokeObjectURL(url);
        reject(new Error('Could not read the logo. Try saving as a flatter JPG or PNG.'));
      };
      img.src = url;
    });
  }

  /* Default identity transform: no zoom, no pan, contain-fit centered.
     v1.19.75 adds optional crop / zoom via the third arg. Centralizing
     the defaults here so callers do not need to remember the shape. */
  var LOGO_TRANSFORM_IDENTITY = { scale: 1, offsetX: 0, offsetY: 0 };
  var LOGO_TRANSFORM_MIN_SCALE = 1;
  var LOGO_TRANSFORM_MAX_SCALE = 3;

  /* Paint a 512x512 JPEG using an existing analysis.
       - bgOverride: pass null to use analysis.autoBg (auto detection).
       - transform: optional crop / zoom. Shape: { scale, offsetX, offsetY }.
                    scale = 1 means contain-fit (default), > 1 zooms in
                    and crops at viewport edges. offsets are in output
                    pixels (512px output space); positive offsetX shifts
                    the image right, positive offsetY shifts it down. */
  function composeLogo(analysis, bgOverride, transform) {
    if (!analysis) return Promise.reject(new Error('No analysis'));
    if (analysis.gif) return Promise.resolve(analysis.file);
    var t = transform || LOGO_TRANSFORM_IDENTITY;
    var scale = Math.max(LOGO_TRANSFORM_MIN_SCALE, Math.min(LOGO_TRANSFORM_MAX_SCALE, t.scale || 1));
    var offX = t.offsetX || 0;
    var offY = t.offsetY || 0;
    return new Promise(function (resolve, reject) {
      try {
        var target = 512;
        var canvas = document.createElement('canvas');
        canvas.width = target; canvas.height = target;
        var ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.fillStyle = bgOverride || analysis.autoBg;
        ctx.fillRect(0, 0, target, target);

        /* Base contain-fit dimensions (scale = 1). Multiply by scale to
           get the actual painted size. Anything outside the 512x512
           target gets clipped by the canvas extent so zoomed-in
           portions effectively crop at the viewport edges. */
        var baseRatio = Math.min(target / analysis.nw, target / analysis.nh);
        var dw = Math.max(1, Math.round(analysis.nw * baseRatio * scale));
        var dh = Math.max(1, Math.round(analysis.nh * baseRatio * scale));
        var dx = Math.floor((target - dw) / 2) + Math.round(offX);
        var dy = Math.floor((target - dh) / 2) + Math.round(offY);
        ctx.drawImage(analysis.img, dx, dy, dw, dh);

        canvas.toBlob(function (blob) {
          if (!blob) return reject(new Error('Image encode failed.'));
          var baseName = (analysis.file.name || 'logo').replace(/\.[^.]+$/, '');
          try {
            var out = new File([blob], baseName + '.jpg', { type: 'image/jpeg', lastModified: Date.now() });
            resolve(out);
          } catch (e) {
            blob.name = baseName + '.jpg';
            resolve(blob);
          }
        }, 'image/jpeg', 0.9);
      } catch (e) {
        reject(e);
      }
    });
  }

  /* Back-compat wrapper. Earlier callers (and the v1.19.69 contract)
     used `normalizeLogo(file) -> File`. New call sites prefer the split
     pair so the analysis can be re-used by the preview. */
  function normalizeLogo(file) {
    return analyzeLogo(file).then(function (a) {
      return a.gif ? a.file : composeLogo(a, null);
    });
  }

  /* Image type validation. Patterns adopted from an earlier form
     after repeated iPhone HEIC failures in the field. We reject explicitly
     client-side with format-specific guidance instead of a generic
     "not an image" so the maker knows what to do next. */
  var ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
  var HEIC_EXTENSIONS = ['.heic', '.heif'];
  var UNSUPPORTED_EXTENSIONS = [
    '.heic', '.heif',
    '.tiff', '.tif',
    '.bmp', '.svg', '.pdf',
    '.raw', '.cr2', '.nef', '.arw', '.dng', '.orf', '.rw2',
  ];
  function getFileExtension(filename) {
    if (!filename) return '';
    var dot = filename.lastIndexOf('.');
    return dot !== -1 ? filename.substring(dot).toLowerCase() : '';
  }
  function validateFileType(file) {
    if (!file) return { valid: false, error: 'No file selected.' };
    var ext = getFileExtension(file.name);

    if (HEIC_EXTENSIONS.indexOf(ext) !== -1) {
      return {
        valid: false,
        error: '"' + file.name + '" is in HEIC format (common on iPhones). Please convert to JPG or PNG before uploading. On iPhone, go to Settings > Camera > Formats > Most Compatible to fix this for future photos.',
      };
    }
    if (UNSUPPORTED_EXTENSIONS.indexOf(ext) !== -1) {
      var typeName = ext.replace('.', '').toUpperCase();
      return {
        valid: false,
        error: '"' + file.name + '" is a ' + typeName + ' file. Please use JPG, PNG, WebP, or GIF.',
      };
    }
    if (file.type && ALLOWED_IMAGE_TYPES.indexOf(file.type) === -1) {
      return {
        valid: false,
        error: '"' + file.name + '" is not a supported image. Please use JPG, PNG, WebP, or GIF.',
      };
    }
    /* Fallback: some Linux desktops report empty file.type for unknowns.
       Accept based on extension if MIME is missing. */
    if (!file.type) {
      var allowedExts = ['.jpg', '.jpeg', '.png', '.webp', '.gif'];
      if (allowedExts.indexOf(ext) === -1) {
        return {
          valid: false,
          error: '"' + file.name + '" is not a recognized image. Please use JPG, PNG, WebP, or GIF.',
        };
      }
    }
    return { valid: true };
  }
  function formatFileSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
    return (bytes / 1024 / 1024).toFixed(1) + ' MB';
  }
  var MAX_PORTFOLIO = 10;

  /* v1.19.97: extension + MIME check is bypassed when a HEIC file gets
     renamed to .jpg, or when a user copies bytes from an iPhone share
     onto a desktop where the file picker reports a wrong MIME. Read
     the first 16 bytes and look for the ISOBMFF `ftyp` box plus a
     HEIC / HEIF brand. If found, reject with HEIC-specific guidance
     so the maker knows what they actually have, not a generic
     "could not be processed" from Shopify after a wasted upload.

     HEIC files always start with a 4-byte big-endian length, then
     the ASCII "ftyp" tag at bytes 4 to 7, then a 4-byte brand at
     bytes 8 to 11. The brand identifies the codec variant. */
  var HEIC_BRANDS = ['heic', 'heix', 'heim', 'heis', 'hevc', 'hevx', 'hevm', 'hevs', 'mif1', 'msf1'];
  function detectHeicFromBytes(file) {
    return new Promise(function (resolve) {
      try {
        file.slice(0, 16).arrayBuffer().then(function (buf) {
          var view = new Uint8Array(buf);
          if (view.length < 12) { resolve(false); return; }
          /* "ftyp" = 0x66 0x74 0x79 0x70 */
          if (view[4] !== 0x66 || view[5] !== 0x74 || view[6] !== 0x79 || view[7] !== 0x70) {
            resolve(false); return;
          }
          var brand = String.fromCharCode(view[8], view[9], view[10], view[11]).toLowerCase();
          resolve(HEIC_BRANDS.indexOf(brand) !== -1);
        }).catch(function () { resolve(false); });
      } catch (e) {
        /* Older browsers without File.slice().arrayBuffer(). Skip the
           magic-byte check rather than block submission. The server's
           FAILED-status handler (v1.6.38) still catches HEIC if it
           reaches Shopify, just with a less specific message. */
        resolve(false);
      }
    });
  }

  /* Friendly HEIC-content error message reused by both file inputs. */
  var HEIC_CONTENT_MESSAGE = ' looks like a HEIC photo (iPhone format) saved with a different extension. Please convert it to a real JPG or PNG before uploading. On a Mac, open the photo in Preview and use File > Export. On an iPhone, share to Files using Most Compatible. Going forward, change Settings > Camera > Formats > Most Compatible so future photos export as JPG.';

  function $(root, sel) { return root.querySelector(sel); }
  function $$(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }

  function readConfig(root) {
    var node = root.querySelector('script[data-mr-config]');
    var empty = { states: [], capabilities: [], project_types: [], badges: [] };
    if (!node) return empty;
    try { return JSON.parse(node.textContent || '{}'); }
    catch (e) { console.error('[maker-register] config parse failed', e); return empty; }
  }

  /* ===== GUIDED PICKER (two-step) =====
     For fields with a one-level hierarchy (capabilities, project types),
     render a two-step flow instead of a dropdown combobox:
       Step A: parent chips shown upfront. Click to toggle.
       Step B: refinement blocks appear only for selected parents that
               have children. If no selected parent has children, Step B
               does not render. This keeps the registration snappy for
               makers whose work is entirely in flat categories.
     Writes to the same hidden input (`[data-combo-value="<key>"]`) so
     the rest of the pipeline (validation, API submission, server-side)
     is unchanged. */
  function bindGuidedPicker(form, key, items) {
    var mount = form.querySelector('[data-combo="' + key + '"]');
    var hidden = form.querySelector('[data-combo-value="' + key + '"]');
    if (!mount || !hidden) return;

    var selected = new Set();
    if (hidden.value) { try { JSON.parse(hidden.value).forEach(function (v) { selected.add(v); }); } catch (e) {} }

    mount.classList.add('maker-register__guided');

    /* Build a flat lookup so we can detect parent-with-children entries
       and resolve child handles back to their parent for cleanup when
       the parent gets deselected. */
    var parentOfChild = {};
    items.forEach(function (it) {
      if (Array.isArray(it.children)) {
        it.children.forEach(function (c) { parentOfChild[c.value] = it.value; });
      }
    });

    function hasChildren(item) {
      return Array.isArray(item.children) && item.children.length > 0;
    }

    function render() {
      mount.innerHTML = '';

      /* Parent chips. The field label + hint above already explain what
         to do, so no extra eyebrow or question here. Keep the section
         clean; the chips ARE the field. */
      var stepA = document.createElement('div');
      stepA.className = 'maker-register__guided-step';
      var grid = document.createElement('div');
      grid.className = 'maker-register__guided-chips';
      items.forEach(function (item) {
        var chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'maker-register__guided-chip';
        chip.dataset.value = item.value;
        if (selected.has(item.value)) chip.classList.add('is-selected');
        chip.appendChild(document.createTextNode(item.label));
        if (hasChildren(item)) {
          var meta = document.createElement('span');
          meta.className = 'maker-register__guided-chip-meta';
          meta.textContent = ' (+' + item.children.length + ')';
          chip.appendChild(meta);
        }
        chip.addEventListener('click', function (ev) {
          ev.preventDefault();
          if (selected.has(item.value)) {
            selected.delete(item.value);
            /* Unselecting a parent also clears any of its children so
               we never persist orphan child handles. */
            if (hasChildren(item)) {
              item.children.forEach(function (c) { selected.delete(c.value); });
            }
          } else {
            selected.add(item.value);
          }
          commit();
        });
        grid.appendChild(chip);
      });
      stepA.appendChild(grid);
      mount.appendChild(stepA);

      /* Step B: refinement blocks per selected parent with children */
      var selectedParentsWithChildren = items.filter(function (it) {
        return selected.has(it.value) && hasChildren(it);
      });
      if (selectedParentsWithChildren.length === 0) return;

      var stepB = document.createElement('div');
      stepB.className = 'maker-register__guided-step maker-register__guided-step--b';
      /* The refinement section opens with just the question + optional
         tag. No procedural "Step B" eyebrow since most makers will not
         see this section at all and labeling it as a step in a sequence
         is misleading. */
      var questionB = document.createElement('p');
      questionB.className = 'maker-register__guided-question';
      questionB.innerHTML = 'Want to add specifics? <span class="maker-register__guided-question-optional">(optional)</span>';
      stepB.appendChild(questionB);
      var refineStack = document.createElement('div');
      refineStack.className = 'maker-register__guided-refine-stack';

      selectedParentsWithChildren.forEach(function (parent) {
        var block = document.createElement('div');
        block.className = 'maker-register__guided-refine-block';
        var title = document.createElement('p');
        title.className = 'maker-register__guided-refine-title';
        var strong = document.createElement('strong');
        strong.textContent = parent.label;
        title.appendChild(strong);
        title.appendChild(document.createTextNode(' specifics:'));
        block.appendChild(title);
        var opts = document.createElement('div');
        opts.className = 'maker-register__guided-refine-options';
        parent.children.forEach(function (child) {
          var rc = document.createElement('button');
          rc.type = 'button';
          rc.className = 'maker-register__guided-refine-chip';
          rc.dataset.value = child.value;
          if (selected.has(child.value)) rc.classList.add('is-selected');
          rc.textContent = child.label;
          rc.addEventListener('click', function (ev) {
            ev.preventDefault();
            if (selected.has(child.value)) selected.delete(child.value);
            else selected.add(child.value);
            commit();
          });
          opts.appendChild(rc);
        });
        block.appendChild(opts);
        refineStack.appendChild(block);
      });
      stepB.appendChild(refineStack);
      mount.appendChild(stepB);
    }

    function commit() {
      hidden.value = JSON.stringify(Array.from(selected));
      mount.classList.remove('is-invalid');
      var err = form.querySelector('[data-error="' + key + '"]');
      if (err) err.textContent = '';
      hidden.dispatchEvent(new Event('change', { bubbles: true }));
      render();
    }

    render();
  }

  /* Detect if items contain any one-level hierarchy. If yes, route to
     the guided two-step picker; otherwise use the type-to-search
     combobox (states, badges). */
  function bindPicker(form, key, items, placeholder) {
    var hasHierarchy = items.some(function (it) {
      return Array.isArray(it.children) && it.children.length > 0;
    });
    if (hasHierarchy) bindGuidedPicker(form, key, items);
    else bindCombobox(form, key, items, placeholder);
  }

  /* ===== COMBOBOX =====
     Multi-select picker with optional one-level hierarchy. An item may
     declare a `children` array; the dropdown then renders the parent
     with a chevron toggle, and the children appear nested below when
     expanded. Picking the parent and picking a child are independent
     selections (a maker can have "CNC Routing" alone, or "CNC: 3-Axis"
     alone, or both at once). The downstream maker-card display already
     groups parent + sub under the Definition Rows pattern. */
  function bindCombobox(form, key, items, placeholder) {
    var mount = form.querySelector('[data-combo="' + key + '"]');
    var hidden = form.querySelector('[data-combo-value="' + key + '"]');
    if (!mount || !hidden) return;

    var selected = new Set();
    if (hidden.value) { try { JSON.parse(hidden.value).forEach(function (v) { selected.add(v); }); } catch (e) {} }

    /* Build a flat lookup so chip rendering can resolve any selected
       handle regardless of whether it is a parent or a child. */
    var flat = [];
    items.forEach(function (it) {
      flat.push(it);
      if (Array.isArray(it.children)) it.children.forEach(function (c) { flat.push(c); });
    });

    var expanded = new Set();
    var hasHierarchy = items.some(function (it) { return Array.isArray(it.children) && it.children.length > 0; });

    mount.classList.add('maker-register__combobox');
    if (hasHierarchy) mount.classList.add('maker-register__combobox--hierarchical');
    var chipsWrap = document.createElement('span'); chipsWrap.style.display = 'contents';
    var input = document.createElement('input');
    input.type = 'text'; input.className = 'maker-register__combo-input';
    input.placeholder = placeholder; input.setAttribute('aria-label', placeholder);
    var menu = document.createElement('ul');
    menu.className = 'maker-register__combo-menu'; menu.setAttribute('role', 'listbox'); menu.hidden = true;
    mount.appendChild(chipsWrap); mount.appendChild(input); mount.appendChild(menu);

    function renderChips() {
      chipsWrap.innerHTML = '';
      selected.forEach(function (val) {
        var item = flat.find(function (i) { return i.value === val; });
        if (!item) return;
        var chip = document.createElement('span'); chip.className = 'maker-register__chip';
        chip.textContent = item.label;
        var x = document.createElement('button'); x.type = 'button';
        x.setAttribute('aria-label', 'Remove ' + item.label);
        x.textContent = String.fromCharCode(0x00d7);
        x.addEventListener('click', function (ev) { ev.stopPropagation(); selected.delete(val); commit(); });
        chip.appendChild(x);
        chipsWrap.appendChild(chip);
      });
    }

    function matchesQuery(label, q) {
      return q === '' || label.toLowerCase().indexOf(q) >= 0;
    }

    function renderMenu() {
      var q = input.value.toLowerCase().trim();
      menu.innerHTML = '';

      var anyVisible = false;

      items.forEach(function (parent) {
        var hasChildren = Array.isArray(parent.children) && parent.children.length > 0;
        var parentMatches = matchesQuery(parent.label, q);
        var matchingChildren = hasChildren
          ? parent.children.filter(function (c) { return matchesQuery(c.label, q); })
          : [];
        /* Show this parent group if the parent label matches OR any
           child label matches. Hide if neither. */
        if (!parentMatches && matchingChildren.length === 0) return;
        anyVisible = true;

        var li = document.createElement('li');
        li.className = 'maker-register__combo-option';
        if (selected.has(parent.value)) li.classList.add('is-selected');
        li.setAttribute('role', 'option');
        li.dataset.value = parent.value;

        var row = document.createElement('div');
        row.className = 'maker-register__combo-row';

        /* Chevron toggle for items with children. Items without
           children get a blank spacer so labels align across rows. */
        if (hasChildren) {
          var chev = document.createElement('button');
          chev.type = 'button';
          chev.className = 'maker-register__combo-chevron';
          var isOpen = expanded.has(parent.value) || (q !== '' && matchingChildren.length > 0);
          chev.setAttribute('aria-label', isOpen ? 'Collapse ' + parent.label : 'Expand ' + parent.label);
          chev.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
          chev.innerHTML = isOpen
            ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="12" height="12" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>'
            : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="12" height="12" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>';
          chev.addEventListener('mousedown', function (ev) {
            ev.preventDefault();
            ev.stopPropagation();
            if (expanded.has(parent.value)) expanded.delete(parent.value);
            else expanded.add(parent.value);
            renderMenu();
          });
          row.appendChild(chev);
        } else if (hasHierarchy) {
          var sp = document.createElement('span');
          sp.className = 'maker-register__combo-spacer';
          sp.setAttribute('aria-hidden', 'true');
          row.appendChild(sp);
        }

        var label = document.createElement('button');
        label.type = 'button';
        label.className = 'maker-register__combo-label';
        label.textContent = parent.label;
        if (hasChildren) label.classList.add('maker-register__combo-label--parent');
        label.addEventListener('mousedown', function (ev) {
          ev.preventDefault();
          if (selected.has(parent.value)) selected.delete(parent.value);
          else selected.add(parent.value);
          input.value = '';
          commit();
        });
        row.appendChild(label);

        li.appendChild(row);

        /* Nested children list. Auto-expanded when the user is typing
           and any child label matches the query. */
        if (hasChildren) {
          var shouldOpen = expanded.has(parent.value) || (q !== '' && matchingChildren.length > 0);
          if (shouldOpen) {
            var subList = document.createElement('ul');
            subList.className = 'maker-register__combo-children';
            var childrenToShow = q === '' ? parent.children : matchingChildren;
            childrenToShow.forEach(function (child) {
              var subLi = document.createElement('li');
              subLi.className = 'maker-register__combo-child';
              if (selected.has(child.value)) subLi.classList.add('is-selected');
              subLi.dataset.value = child.value;

              var subBtn = document.createElement('button');
              subBtn.type = 'button';
              subBtn.className = 'maker-register__combo-child-label';
              subBtn.textContent = child.label;
              subBtn.addEventListener('mousedown', function (ev) {
                ev.preventDefault();
                if (selected.has(child.value)) selected.delete(child.value);
                else selected.add(child.value);
                input.value = '';
                commit();
              });
              subLi.appendChild(subBtn);
              subList.appendChild(subLi);
            });
            li.appendChild(subList);
          }
        }

        menu.appendChild(li);
      });

      if (!anyVisible) {
        var empty = document.createElement('li');
        empty.className = 'maker-register__combo-option maker-register__combo-option--empty';
        empty.textContent = q ? 'No matches' : (selected.size === flat.length ? 'All selected' : 'Type to search');
        menu.appendChild(empty);
      }
    }

    function commit() {
      hidden.value = JSON.stringify(Array.from(selected));
      mount.classList.remove('is-invalid');
      var err = form.querySelector('[data-error="' + key + '"]');
      if (err) err.textContent = '';
      renderChips(); renderMenu();
      hidden.dispatchEvent(new Event('change', { bubbles: true }));
    }
    function open() { menu.hidden = false; renderMenu(); }
    function close() { menu.hidden = true; }

    mount.addEventListener('click', function () { input.focus(); });
    input.addEventListener('focus', open);
    input.addEventListener('blur', function () { setTimeout(close, 120); });
    input.addEventListener('input', function () { renderMenu(); menu.hidden = false; });
    input.addEventListener('keydown', function (ev) {
      if (ev.key === 'Backspace' && input.value === '' && selected.size > 0) {
        var last = Array.from(selected).pop(); selected.delete(last); commit();
      } else if (ev.key === 'Enter') {
        ev.preventDefault();
        /* First match the user can act on: a non-empty option OR a
           visible child label. Try the first row's primary label. */
        var first = menu.querySelector('.maker-register__combo-option:not(.maker-register__combo-option--empty)');
        if (first && first.dataset.value) { selected.add(first.dataset.value); input.value = ''; commit(); }
      } else if (ev.key === 'Escape') { close(); input.blur(); }
    });
    commit();
  }

  /* ===== PREFERRED CONTACT GATING ===== */
  function bindPreferredContact(form) {
    var sel = form.querySelector('[data-preferred]');
    if (!sel) return;
    function recompute() {
      $$(sel, 'option[data-needs]').forEach(function (opt) {
        var fld = form.querySelector('[name="' + opt.dataset.needs + '"]');
        var hasValue = fld && fld.value && fld.value.trim();
        opt.disabled = !hasValue;
      });
    }
    $$(form, 'input[name^="social_"], input[name="website"], input[name="email"], input[name="phone_number"]').forEach(function (fld) {
      fld.addEventListener('input', recompute);
      fld.addEventListener('blur', recompute);
    });
    recompute();
  }

  /* ===== CHAR COUNTER ===== */
  function bindCharCount(form) {
    var ta = form.querySelector('textarea[name="description"]');
    var out = form.querySelector('[data-char-count]');
    if (!ta || !out) return;
    function update() {
      out.textContent = ta.value.length + ' / 1500' + (ta.value.length < 100 ? ' (min 100)' : '');
    }
    ta.addEventListener('input', update); update();
  }

  /* ===== PHONE FORMATTER ===== */
  /* Phone formatting. Adopted after field testing showed
     blur-only formatting felt sluggish: the user typed digits and the field
     looked broken until they tabbed away. Live formatting reshapes the value
     after every keystroke. Strips a leading `1` country code if the user
     pasted an 11-digit number. */
  function bindPhone(form) {
    var input = form.querySelector('[data-phone]');
    if (!input) return;
    function formatPartial(value) {
      var digits = (value || '').replace(/\D/g, '');
      if (digits.length > 10 && digits.charAt(0) === '1') digits = digits.substring(1);
      if (digits.length > 10) digits = digits.substring(0, 10);
      var out = '';
      if (digits.length > 0) out = '(' + digits.substring(0, 3);
      if (digits.length >= 3) out += ') ';
      if (digits.length > 3) out += digits.substring(3, 6);
      if (digits.length >= 6) out += '-';
      if (digits.length > 6) out += digits.substring(6, 10);
      return out;
    }
    input.addEventListener('input', function () {
      var raw = input.value;
      var formatted = formatPartial(raw);
      if (formatted !== raw) input.value = formatted;
    });
    input.addEventListener('blur', function () {
      input.value = formatPartial(input.value);
    });
  }

  /* ===== FILE MANAGER ===== */
  function fmtSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
    return (bytes / 1024 / 1024).toFixed(1) + ' MB';
  }

  /* Website normalization. On blur,
     strip an http(s):// prefix and a leading www., trim a trailing slash,
     and lowercase the domain. Leave path/query alone because makers often
     point to a shop subpath (etsy.com/shop/acmecarving, squarespace path).
     This way the stored value is canonical and the directory link still
     works. */
  function bindWebsite(form) {
    var input = form.querySelector('[name="website"]');
    if (!input) return;
    input.addEventListener('blur', function () {
      var v = (input.value || '').trim();
      if (!v) return;
      v = v.replace(/^[a-z]+:\/\//i, '');
      v = v.replace(/^www\./i, '');
      v = v.replace(/\/+$/, '');
      var slashIdx = v.indexOf('/');
      if (slashIdx === -1) {
        v = v.toLowerCase();
      } else {
        v = v.substring(0, slashIdx).toLowerCase() + v.substring(slashIdx);
      }
      input.value = v;
    });
  }

  /* v1.19.73: live preview mockup.

     Once the maker picks a logo, `analyzeLogo` caches the decoded
     source image on the form. This renderer paints a small mock maker
     card into the `[data-logo-preview]` container so the maker can see
     exactly how their logo will land on the directory grid BEFORE
     they submit. Background-swatch pills let them override the
     auto-picked bg color; the company-name + city fields live-update
     the preview text via a debounced delegated listener (see
     `bindLogoPreviewLiveText`).

     Re-rendering rules:
       - Called on initial compose, on bg-swatch click, and on logo
         remove. The remove path clears `__logoFile` + `__logoAnalysis`
         and the renderer treats that as "no logo, hide preview".
       - The Blob URL the preview img points at is tracked on
         `form.__logoPreviewUrl` so we can revoke it cleanly when the
         user picks a different bg (avoids growing the leak per click).
       - Maker-supplied text (company name, city) is set via
         textContent, never innerHTML, so a typed `<script>` cannot
         escape the preview into the page. */
  function renderLogoPreview(form) {
    var container = form.querySelector('[data-logo-preview]');
    if (!container) return;

    if (!form.__logoFile || !form.__logoAnalysis) {
      container.hidden = true;
      container.innerHTML = '';
      if (form.__logoPreviewUrl) {
        try { URL.revokeObjectURL(form.__logoPreviewUrl); } catch (e) {}
        form.__logoPreviewUrl = null;
      }
      return;
    }

    if (form.__logoPreviewUrl) {
      try { URL.revokeObjectURL(form.__logoPreviewUrl); } catch (e) {}
    }
    form.__logoPreviewUrl = URL.createObjectURL(form.__logoFile);

    if (!form.__logoTransform) {
      form.__logoTransform = { scale: 1, offsetX: 0, offsetY: 0 };
    }
    var zoomPct = Math.round(form.__logoTransform.scale * 100);

    container.hidden = false;
    container.innerHTML =
      '<p class="maker-register__logo-preview-label">Adjust your logo</p>' +
      '<div class="maker-register__logo-editor-wrap">' +
        '<div class="maker-register__logo-editor"' +
             ' data-preview-editor' +
             ' role="application"' +
             ' tabindex="0"' +
             ' aria-label="Logo editor. Drag to reposition. Use the zoom slider below to zoom in or out.">' +
          '<img data-preview-img alt="" draggable="false" />' +
          '<div class="maker-register__logo-editor-hint" aria-hidden="true">Drag to reposition</div>' +
        '</div>' +
      '</div>' +
      '<div class="maker-register__logo-zoom">' +
        '<button type="button" class="maker-register__logo-zoom-btn" data-zoom-step="-10" aria-label="Zoom out">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="14" height="14" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/></svg>' +
        '</button>' +
        '<input type="range" class="maker-register__logo-zoom-slider"' +
               ' data-preview-zoom min="100" max="300" step="5"' +
               ' value="' + zoomPct + '" aria-label="Zoom level (' + zoomPct + ' percent)" />' +
        '<button type="button" class="maker-register__logo-zoom-btn" data-zoom-step="10" aria-label="Zoom in">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="14" height="14" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>' +
        '</button>' +
        '<span class="maker-register__logo-zoom-value" data-zoom-readout>' + zoomPct + '%</span>' +
        '<button type="button" class="maker-register__logo-reset" data-preview-reset>Reset</button>' +
      '</div>' +
      '<p class="maker-register__logo-preview-card-label">Card preview</p>' +
      '<div class="maker-register__logo-preview-card" role="img" aria-label="Preview of your maker card">' +
        '<div class="maker-register__logo-preview-avatar">' +
          '<img data-preview-card-img alt="" />' +
        '</div>' +
        '<div class="maker-register__logo-preview-info">' +
          '<strong class="maker-register__logo-preview-name" data-preview-name></strong>' +
          '<p class="maker-register__logo-preview-city">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="11" height="11" aria-hidden="true"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg> ' +
            '<span data-preview-city></span>' +
          '</p>' +
        '</div>' +
      '</div>';

    /* Both the large editor image and the small card-preview image
       point at the same composed file. Re-compose updates both. */
    var editorImg = container.querySelector('[data-preview-img]');
    var cardPreviewImg = container.querySelector('[data-preview-card-img]');
    if (editorImg) editorImg.src = form.__logoPreviewUrl;
    if (cardPreviewImg) cardPreviewImg.src = form.__logoPreviewUrl;
    updateLogoPreviewText(form);

    bindLogoEditor(form, container);
  }

  /* Hook up drag-to-pan + wheel-to-zoom + slider + reset for the logo
     editor. The editor is a CSS-sized box (180x180 desktop, 150x150
     under 500px, 140x140 under 380px) that displays the composed 512x512
     logo scaled down. Pointer-event drag and wheel-zoom translate into
     transform-state mutations on `form.__logoTransform`; a debounced
     recompose pushes the new file into the storefront upload pipeline. */
  function bindLogoEditor(form, container) {
    var editor = container.querySelector('[data-preview-editor]');
    var slider = container.querySelector('[data-preview-zoom]');
    var readout = container.querySelector('[data-zoom-readout]');
    var resetBtn = container.querySelector('[data-preview-reset]');
    var stepBtns = container.querySelectorAll('[data-zoom-step]');
    if (!editor || !slider || !resetBtn) return;

    function updateReadout() {
      var pct = Math.round(form.__logoTransform.scale * 100);
      if (readout) readout.textContent = pct + '%';
      if (slider) {
        slider.value = pct;
        slider.setAttribute('aria-label', 'Zoom level (' + pct + ' percent)');
      }
    }

    function clampTransform() {
      var analysis = form.__logoAnalysis;
      if (!analysis) return;
      var target = 512;
      var baseRatio = Math.min(target / analysis.nw, target / analysis.nh);
      var dw = analysis.nw * baseRatio * form.__logoTransform.scale;
      var dh = analysis.nh * baseRatio * form.__logoTransform.scale;
      /* Allow the image to be dragged up to half-out on each axis so
         the maker can crop to an edge while at least 50% of the
         painted image stays visible. */
      var maxOffX = Math.max(0, (dw + target) / 2 - target / 4);
      var maxOffY = Math.max(0, (dh + target) / 2 - target / 4);
      form.__logoTransform.offsetX = Math.max(-maxOffX, Math.min(maxOffX, form.__logoTransform.offsetX));
      form.__logoTransform.offsetY = Math.max(-maxOffY, Math.min(maxOffY, form.__logoTransform.offsetY));
    }

    function scheduleRecompose() {
      clearTimeout(form.__logoRecomposeTimer);
      form.__logoRecomposeTimer = setTimeout(function () {
        if (!form.__logoAnalysis) return;
        composeLogo(form.__logoAnalysis, null, form.__logoTransform).then(function (newFile) {
          form.__logoFile = newFile;
          if (form.__logoPreviewUrl) {
            try { URL.revokeObjectURL(form.__logoPreviewUrl); } catch (e) {}
          }
          form.__logoPreviewUrl = URL.createObjectURL(newFile);
          var editorImg = container.querySelector('[data-preview-img]');
          if (editorImg) editorImg.src = form.__logoPreviewUrl;
          var cardImg2 = container.querySelector('[data-preview-card-img]');
          if (cardImg2) cardImg2.src = form.__logoPreviewUrl;
          var fileCardImg = form.querySelector('[data-logo-preview-img]');
          if (fileCardImg) fileCardImg.src = form.__logoPreviewUrl;
        }).catch(function (err) {
          console.error('[maker-register] re-compose failed', err);
        });
      }, 60);
    }

    var dragging = false;
    var startX = 0, startY = 0, startOffX = 0, startOffY = 0;
    var pxToOutput = 1;

    editor.addEventListener('pointerdown', function (e) {
      if (!form.__logoAnalysis) return;
      e.preventDefault();
      dragging = true;
      try { editor.setPointerCapture(e.pointerId); } catch (_) {}
      var rect = editor.getBoundingClientRect();
      pxToOutput = rect.width > 0 ? 512 / rect.width : 1;
      startX = e.clientX;
      startY = e.clientY;
      startOffX = form.__logoTransform.offsetX;
      startOffY = form.__logoTransform.offsetY;
      editor.classList.add('is-dragging');
    });

    editor.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = (e.clientX - startX) * pxToOutput;
      var dy = (e.clientY - startY) * pxToOutput;
      form.__logoTransform.offsetX = startOffX + dx;
      form.__logoTransform.offsetY = startOffY + dy;
      clampTransform();
      scheduleRecompose();
    });

    function endDrag(e) {
      if (!dragging) return;
      dragging = false;
      try { editor.releasePointerCapture(e.pointerId); } catch (_) {}
      editor.classList.remove('is-dragging');
    }
    editor.addEventListener('pointerup', endDrag);
    editor.addEventListener('pointercancel', endDrag);

    editor.addEventListener('wheel', function (e) {
      if (!form.__logoAnalysis) return;
      e.preventDefault();
      var step = e.deltaY > 0 ? -0.05 : 0.05;
      var next = (form.__logoTransform.scale || 1) + step;
      form.__logoTransform.scale = Math.max(LOGO_TRANSFORM_MIN_SCALE, Math.min(LOGO_TRANSFORM_MAX_SCALE, next));
      clampTransform();
      updateReadout();
      scheduleRecompose();
    }, { passive: false });

    slider.addEventListener('input', function () {
      var pct = parseInt(slider.value, 10) || 100;
      form.__logoTransform.scale = Math.max(LOGO_TRANSFORM_MIN_SCALE, Math.min(LOGO_TRANSFORM_MAX_SCALE, pct / 100));
      clampTransform();
      updateReadout();
      scheduleRecompose();
    });

    stepBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var step = parseInt(btn.dataset.zoomStep, 10) || 0;
        var cur = parseInt(slider.value, 10) || 100;
        var next = Math.max(100, Math.min(300, cur + step));
        slider.value = next;
        form.__logoTransform.scale = next / 100;
        clampTransform();
        updateReadout();
        scheduleRecompose();
      });
    });

    resetBtn.addEventListener('click', function () {
      form.__logoTransform = { scale: 1, offsetX: 0, offsetY: 0 };
      updateReadout();
      scheduleRecompose();
    });
  }

  /* Live-update the preview's company name and city when the maker
     types in the corresponding form fields. textContent only; never
     interpolate raw input into HTML. */
  function updateLogoPreviewText(form) {
    var container = form.querySelector('[data-logo-preview]');
    if (!container || container.hidden) return;
    var companyInput = form.querySelector('[name="company_name"]');
    var cityInput = form.querySelector('[name="city"]');
    var nameEl = container.querySelector('[data-preview-name]');
    var cityEl = container.querySelector('[data-preview-city]');
    var company = (companyInput && companyInput.value && companyInput.value.trim()) || 'Your shop';
    var city = (cityInput && cityInput.value && cityInput.value.trim()) || 'Your city';
    if (nameEl) nameEl.textContent = company;
    if (cityEl) cityEl.textContent = city;
  }

  /* Attach a delegated `input` listener on the form so any keystroke
     in `company_name` or `city` debounces a preview text update.
     Idempotent: a `__previewLiveTextBound` flag prevents double-binding
     if bindLogo gets called multiple times (e.g. after a successful
     submit, which re-binds the file pickers). */
  function bindLogoPreviewLiveText(form) {
    if (form.__previewLiveTextBound) return;
    form.__previewLiveTextBound = true;
    form.addEventListener('input', function (e) {
      var t = e.target;
      if (!t || !t.name) return;
      if (t.name !== 'company_name' && t.name !== 'city') return;
      clearTimeout(form.__previewTextTimer);
      form.__previewTextTimer = setTimeout(function () {
        updateLogoPreviewText(form);
      }, 150);
    });
  }

  function bindLogo(form) {
    var card = form.querySelector('[data-logo-card]');
    var input = form.querySelector('[data-logo-input]');
    if (!card || !input) return;
    bindLogoPreviewLiveText(form);
    /* Reset the managed logo file on every (re)bind so a stale entry
       from a previous successful submission cannot leak into the next
       one. v1.19.75: also reset the crop transform so the next picked
       logo starts at contain-fit. */
    form.__logoFile = null;
    form.__logoAnalysis = null;
    form.__logoTransform = { scale: 1, offsetX: 0, offsetY: 0 };
    renderLogoPreview(form);

    function clearError() {
      var alert = form.querySelector('[data-alert]');
      if (alert && alert.classList.contains('maker-register__alert--error')) {
        alert.hidden = true; alert.textContent = '';
      }
      /* Clear the inline required-field error and invalid state when
         the user picks a logo, so they get instant positive feedback. */
      var inlineErr = form.querySelector('[data-error="image"]');
      if (inlineErr) inlineErr.textContent = '';
      if (input) input.classList.remove('is-invalid');
    }
    function render() {
      var file = input.files && input.files[0];
      if (!file) {
        card.classList.remove('maker-register__file-card--with-logo');
        card.innerHTML = '<div class="maker-register__file-empty">' +
          '<svg class="maker-register__file-empty-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="24" height="24"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>' +
          '<p class="maker-register__file-empty-text"><strong>Upload your shop logo</strong></p>' +
          '<p class="maker-register__file-empty-meta">JPG, PNG, WebP, or GIF. Up to 10MB. Square works best.</p>' +
          '<button type="button" class="maker-register__file-pick" data-logo-pick>Choose file</button>' +
          '</div>';
        bindLogoPick(); return;
      }
      var url = URL.createObjectURL(file);
      card.classList.add('maker-register__file-card--with-logo');
      card.innerHTML = '<div class="maker-register__file-logo">' +
        '<img alt="Logo preview" data-logo-preview-img />' +
        '<div class="maker-register__file-info">' +
        '<div class="maker-register__file-name">' + escapeHtml(file.name) + '</div>' +
        '<div class="maker-register__file-size">' + fmtSize(file.size) + '</div>' +
        '</div>' +
        '</div>' +
        '<div class="maker-register__file-actions">' +
        '<button type="button" class="maker-register__file-btn" data-logo-pick>Replace</button>' +
        '<button type="button" class="maker-register__file-btn maker-register__file-btn--danger" data-logo-remove>Remove</button>' +
        '</div>';
      var img = card.querySelector('[data-logo-preview-img]');
      img.src = url; img.onload = function () { URL.revokeObjectURL(url); };
      bindLogoPick();
      var rm = card.querySelector('[data-logo-remove]');
      if (rm) rm.addEventListener('click', function () {
        input.value = '';
        form.__logoFile = null;
        form.__logoAnalysis = null;
        render();
        renderLogoPreview(form);
      });
    }
    function bindLogoPick() {
      var pick = card.querySelector('[data-logo-pick]');
      if (pick) pick.addEventListener('click', function () { input.click(); });
    }
    input.addEventListener('change', function () {
      clearError();
      var f = input.files && input.files[0];
      if (f) {
        var typeCheck = validateFileType(f);
        if (!typeCheck.valid) {
          showAlert(form, typeCheck.error, 'error');
          input.value = ''; form.__logoFile = null; render(); return;
        }
        if (f.size > MAX_FILE_SIZE) {
          showAlert(form, 'Logo is too large (' + formatFileSize(f.size) + '). Please use an image under 10MB.', 'error');
          input.value = ''; form.__logoFile = null; render(); return;
        }
        /* v1.19.97: catch HEIC content under a renamed extension before
           we waste a Canvas decode + upload attempt that Shopify would
           reject anyway. Magic-byte check is async but only reads 16
           bytes; runs in single-digit milliseconds even on phones.

           Chain flow: HEIC check -> analyzeLogo -> composeLogo -> set
           form state. If HEIC content is detected, we resolve to
           `null` so the next `.then` short-circuits without running
           analyzeLogo. Errors from any link land in the shared catch. */
        detectHeicFromBytes(f).then(function (isHeic) {
          if (isHeic) {
            showAlert(form, '"' + f.name + '"' + HEIC_CONTENT_MESSAGE, 'error');
            input.value = ''; form.__logoFile = null; render();
            return null;
          }
          return analyzeLogo(f);
        }).then(function (analysis) {
          if (!analysis) return;
          form.__logoAnalysis = analysis;
          form.__logoTransform = { scale: 1, offsetX: 0, offsetY: 0 };
          return composeLogo(analysis, null, form.__logoTransform).then(function (prepared) {
            form.__logoFile = prepared;
            render();
            renderLogoPreview(form);
          });
        }).catch(function (err) {
          showAlert(form, err.message || 'Could not read that logo. Try saving as a flatter JPG or PNG.', 'error');
          input.value = '';
          form.__logoFile = null;
          form.__logoAnalysis = null;
          render();
          renderLogoPreview(form);
        });
        return;
      }
      form.__logoFile = null;
      render();
    });
    render();
  }

  function bindPortfolio(form) {
    var card = form.querySelector('[data-portfolio-card]');
    var input = form.querySelector('[data-portfolio-input]');
    if (!card || !input) return;

    // Internal file array so the user can remove individual items.
    form.__portfolioFiles = [];

    function clearError() {
      var alert = form.querySelector('[data-alert]');
      if (alert && alert.classList.contains('maker-register__alert--error')) {
        alert.hidden = true; alert.textContent = '';
      }
      /* Inline error + invalid state are cleared AFTER files actually
         land in `form.__portfolioFiles` (see change handler below).
         Running the clear here was a no-op on first upload because
         the array length read was the OLD length, before the new
         files were added. */
    }
    function render() {
      var files = form.__portfolioFiles || [];
      var html = '';
      if (files.length === 0) {
        html += '<div class="maker-register__file-empty">' +
          '<svg class="maker-register__file-empty-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="24" height="24"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>' +
          '<p class="maker-register__file-empty-text"><strong>Add portfolio photos</strong></p>' +
          '<p class="maker-register__file-empty-meta">Up to ' + MAX_PORTFOLIO + ' images. JPG, PNG, WebP, or GIF. 10MB each.</p>' +
          '<button type="button" class="maker-register__file-pick" data-portfolio-pick>Choose files</button>' +
          '</div>';
      } else {
        html += '<div class="maker-register__portfolio-grid" data-portfolio-grid></div>';
        if (files.length < MAX_PORTFOLIO) {
          html += '<button type="button" class="maker-register__portfolio-add" data-portfolio-pick>' +
            '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>' +
            'Add more</button>';
        }
        html += '<p class="maker-register__portfolio-count">' + files.length + ' of ' + MAX_PORTFOLIO + ' uploaded</p>';
      }
      card.innerHTML = html;
      bindPick();
      if (files.length > 0) {
        var grid = card.querySelector('[data-portfolio-grid]');
        files.forEach(function (file, idx) {
          var item = document.createElement('div');
          item.className = 'maker-register__portfolio-item';
          var url = URL.createObjectURL(file);
          var img = document.createElement('img');
          img.src = url; img.alt = file.name; img.loading = 'lazy';
          img.onload = function () { URL.revokeObjectURL(url); };
          var rm = document.createElement('button');
          rm.type = 'button'; rm.className = 'maker-register__portfolio-remove';
          rm.setAttribute('aria-label', 'Remove ' + file.name);
          rm.textContent = String.fromCharCode(0x00d7);
          rm.addEventListener('click', function () {
            form.__portfolioFiles.splice(idx, 1); render();
          });
          item.appendChild(img); item.appendChild(rm); grid.appendChild(item);
        });
      }
    }
    function bindPick() {
      var pick = card.querySelector('[data-portfolio-pick]');
      if (pick) pick.addEventListener('click', function () { input.click(); });
    }
    input.addEventListener('change', function () {
      clearError();
      var newFiles = Array.prototype.slice.call(input.files || []);
      var current = form.__portfolioFiles || [];

      /* Accumulate all errors across the picked batch so the user does not
         have to dismiss one alert and re-pick one file at a time. Common
         scenario: maker selects 6 photos, two of which are HEIC. Tell them
         both filenames at once. */
      var errors = [];
      if (newFiles.length + current.length > MAX_PORTFOLIO) {
        errors.push('Too many files. You can upload up to ' + MAX_PORTFOLIO + ' images (' + (MAX_PORTFOLIO - current.length) + ' more allowed).');
      }
      for (var i = 0; i < newFiles.length; i++) {
        var typeCheck = validateFileType(newFiles[i]);
        if (!typeCheck.valid) {
          errors.push(typeCheck.error);
        } else if (newFiles[i].size > MAX_FILE_SIZE) {
          errors.push('"' + newFiles[i].name + '" is too large (' + formatFileSize(newFiles[i].size) + '). Max 10MB per image.');
        }
      }
      if (errors.length > 0) {
        showAlert(form, errors.join(' '), 'error');
        input.value = ''; return;
      }
      /* v1.19.97: magic-byte HEIC sweep across the whole picked batch
         before any Canvas decoding happens. Same rationale as the
         logo path: HEIC content under a renamed extension would slip
         past extension + MIME checks and burn cycles plus a wasted
         upload attempt. Reading 16 bytes per file is cheap. */
      Promise.all(newFiles.map(function (f) {
        return detectHeicFromBytes(f).then(function (isHeic) {
          return { file: f, isHeic: isHeic };
        });
      })).then(function (checked) {
        var heic = checked.filter(function (c) { return c.isHeic; });
        if (heic.length > 0) {
          var names = heic.map(function (c) { return '"' + c.file.name + '"'; });
          var prefix = heic.length === 1 ? names[0] : (names.join(', '));
          showAlert(form, prefix + HEIC_CONTENT_MESSAGE, 'error');
          input.value = '';
          return;
        }
        /* Canvas-normalize every picked file in parallel before
           pushing into the managed portfolio array. Same rationale as
           the logo path: strips ICC profiles + EXIF + non-standard
           chunks so Shopify Files processing accepts the result. */
        Promise.all(newFiles.map(function (f) { return preprocessImage(f); }))
          .then(function (prepared) {
            form.__portfolioFiles = current.concat(prepared);
            input.value = '';
            var inlineErr = form.querySelector('[data-error="portfolio_images"]');
            if (inlineErr) inlineErr.textContent = '';
            if (card) card.classList.remove('is-invalid');
            render();
          })
          .catch(function (err) {
            showAlert(form, err.message || 'One of the images could not be read. Try saving as flatter JPGs or PNGs.', 'error');
            input.value = '';
          });
      });
    });
    render();
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ===== INLINE VALIDATION =====
     One validator per named field. Returns true if valid (or optional + empty),
     false if invalid. Always writes a SPECIFIC error message into the named
     [data-error] container (which has aria-live="polite") so a screen reader
     announces the failure. Required fields with empty values get a tailored
     prompt instead of a generic "Required." */
  var REQUIRED_FIELDS = {
    company_name: 'Tell us your shop or company name.',
    description: 'Add a short paragraph about your shop. Buyers read this on your profile.',
    city: 'Which city are you based in?',
    email: 'Email is required so buyers can reach you.',
    preferred_contact: 'Pick the channel you want buyers to use first.',
    image: 'Upload a shop logo so buyers can recognize your work.',
    portfolio_images: 'Add at least one portfolio photo so buyers can see your work.',
  };
  function validateField(form, fld) {
    var name = fld.name;
    if (!name) return true;
    var errEl = form.querySelector('[data-error="' + name + '"]');
    var val = (fld.value || '').trim();
    var required = fld.hasAttribute('required');
    var msg = '';

    if (required && !val) {
      msg = REQUIRED_FIELDS[name] || 'This field is required.';
    } else if (val) {
      switch (name) {
        case 'email':
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) msg = 'That email does not look right. Try the format name@example.com.';
          else if (val.length > 254) msg = 'Email must be under 254 characters.';
          break;
        case 'phone_number':
          var digits = val.replace(/\D/g, '');
          /* Accept an 11-digit number whose first digit is 1; that is a US
             country-code prefix the live formatter strips on the next input
             event. Without this branch the user types 1-555-... and sees a
             validation error mid-keystroke. */
          if (digits.length === 11 && digits.charAt(0) === '1') digits = digits.substring(1);
          if (digits.length !== 10) msg = 'Phone number must have 10 digits (US format).';
          break;
        case 'description':
          if (val.length < 100) msg = 'Description must be at least 100 characters. Currently ' + val.length + '.';
          else if (val.length > 1500) msg = 'Description must be under 1,500 characters. Currently ' + val.length + '.';
          break;
        case 'company_name':
          if (val.length > 200) msg = 'Company name must be under 200 characters.';
          break;
        case 'city':
          if (val.length > 100) msg = 'City must be under 100 characters.';
          break;
        case 'website':
          if (val.length > 500) msg = 'Website URL must be under 500 characters.';
          else {
            /* Strict domain check. Strip
               protocol / www, then look at only the domain portion before
               the first slash so paths like etsy.com/shop/foo still pass. */
            var w = val.toLowerCase().replace(/^[a-z]+:\/\//, '').replace(/^www\./, '');
            var domain = w.split('/')[0].replace(/[?#].*$/, '').replace(/\.+$/, '');
            var domainRe = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*\.[a-z]{2,}$/;
            if (!domainRe.test(domain)) msg = 'Enter a website like acmecarving.com or https://acmecarving.com.';
          }
          break;
        case 'business_license':
          if (val.length > 200) msg = 'Business / LLC name must be under 200 characters.';
          break;
        case 'year_started':
          var yr = parseInt(val, 10);
          if (isNaN(yr)) msg = 'Year must be a number.';
          else if (yr < 1900) msg = 'Year must be 1900 or later.';
          else if (yr > 2100) msg = 'Year must be 2100 or earlier.';
          break;
        case 'social_instagram':
        case 'social_tiktok':
          if (val.length > 100) msg = 'Handle must be under 100 characters.';
          break;
        case 'social_youtube':
          if (val.length > 200) msg = 'Handle must be under 200 characters.';
          break;
        case 'social_facebook':
          if (val.length > 200) {
            msg = 'Facebook URL must be under 200 characters.';
          } else if (val) {
            /* v1.19.76: real makers paste their Facebook page NAME ("My
               Cool Shop") instead of the URL ("facebook.com/MyCoolShop").
               Accept any value that contains a Facebook domain or a
               slash (which implies a URL path); reject the rest with a
               specific helpful error pointing at the URL format. */
            var fbLower = val.toLowerCase();
            var looksLikeFbUrl = /facebook\.com|fb\.com|fb\.me/.test(fbLower);
            var looksLikePath = val.indexOf('/') !== -1;
            if (!looksLikeFbUrl && !looksLikePath) {
              msg = 'Paste your full Facebook page URL, not just the page name. Example: facebook.com/yourpage';
            } else if (/(?:^|\/)share\//i.test(fbLower)) {
              /* v1.19.77: Facebook share-button links (facebook.com/share/SHORT_CODE)
                 are opaque short-redirects that work in a browser but store as
                 gibberish. The api now rejects them server-side; rejecting here too
                 saves users a round-trip and gives them clearer guidance: open the
                 share link in a browser tab and copy the URL from the address bar
                 after it redirects. That final URL is what we want stored. */
              msg = 'Share-button links are not accepted. Open the link in a browser, then copy the URL from the address bar (facebook.com/yourpage or facebook.com/profile.php?id=NUMBER) and paste that instead.';
            }
          }
          break;
        case 'capabilities_custom':
        case 'project_types_custom':
          if (val.length > 300) msg = 'Optional notes must be under 300 characters. Currently ' + val.length + '.';
          break;
      }
    }

    if (errEl) errEl.textContent = msg;
    fld.classList.toggle('is-invalid', !!msg);
    return !msg;
  }
  function bindInlineValidation(form) {
    /* Blur fires inline validation. Bind every named text field so a format
       issue (year out of range, website typo, custom-text too long, etc.)
       surfaces as the user moves between fields, not on Submit. */
    $$(form, 'input[name], textarea[name]').forEach(function (fld) {
      if (fld.type === 'hidden' || fld.type === 'file') return;
      fld.addEventListener('blur', function () { validateField(form, fld); });
    });
  }

  /* Social handle inputs render a non-editable "@" prefix inside the input
     box. If the user types or pastes "@" we strip it so the stored value is
     never doubled (@@yourhandle). Also strips a leading slash for safety. */
  function bindStripAt(form) {
    $$(form, 'input[data-strip-at]').forEach(function (fld) {
      function strip() {
        var raw = fld.value || '';
        var cleaned = raw.replace(/^[@\/]+/, '');
        if (cleaned !== raw) fld.value = cleaned;
      }
      fld.addEventListener('input', strip);
      fld.addEventListener('paste', function () { setTimeout(strip, 0); });
      fld.addEventListener('blur', strip);
    });
  }

  /* v1.19.76: strip all whitespace from URL-bearing inputs (website +
     all four socials). URLs and social handles never legitimately
     contain spaces, but real makers paste with stray leading / trailing
     whitespace or accidentally type a space and end up with a broken
     value (we have seen "yourshop .com" land in metaobject data).
     Strips on input, paste, and blur; preserves caret position
     approximately so the cursor does not jump to the end of the field
     in the middle of typing. */
  /* v1.20.8: clear the mandatory safety-acknowledgment error the moment
     the maker ticks the box, so they get immediate feedback after a
     blocked submit instead of waiting for the next submit attempt. */
  function bindSafetyAck(form) {
    var ackBox = form.querySelector('[name="security_acknowledgment"]');
    if (!ackBox) return;
    ackBox.addEventListener('change', function () {
      if (!ackBox.checked) return;
      var ackErr = form.querySelector('[data-error="security_acknowledgment"]');
      if (ackErr) ackErr.textContent = '';
      var ackRow = ackBox.closest('.maker-register__ack');
      if (ackRow) ackRow.classList.remove('is-invalid');
    });
  }

  function bindNoSpaces(form) {
    $$(form, 'input[data-no-spaces]').forEach(function (fld) {
      function strip() {
        var raw = fld.value || '';
        var cleaned = raw.replace(/\s+/g, '');
        if (cleaned === raw) return;
        var caret = (typeof fld.selectionStart === 'number') ? fld.selectionStart : null;
        var removedBeforeCaret = 0;
        if (caret !== null) {
          for (var i = 0; i < Math.min(caret, raw.length); i++) {
            if (/\s/.test(raw.charAt(i))) removedBeforeCaret++;
          }
        }
        fld.value = cleaned;
        if (caret !== null) {
          var newCaret = Math.max(0, caret - removedBeforeCaret);
          try { fld.setSelectionRange(newCaret, newCaret); } catch (_) {}
        }
      }
      fld.addEventListener('input', strip);
      fld.addEventListener('paste', function () { setTimeout(strip, 0); });
      fld.addEventListener('blur', strip);
    });
  }

  /* ===== ALERT + BUSY ===== */
  function showAlert(form, msg, kind) {
    var node = form.querySelector('[data-alert]');
    if (!node) return;
    node.hidden = false;
    node.className = 'maker-register__alert maker-register__alert--' + kind;
    node.textContent = msg;
    node.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
  function hideAlert(form) { var n = form.querySelector('[data-alert]'); if (n) { n.hidden = true; n.textContent = ''; n.className = 'maker-register__alert'; } }
  function setBusy(form, busy) {
    var btn = form.querySelector('[data-submit]');
    if (!btn) return;
    /* Never re-enable once the form has been successfully submitted. */
    if (form.__submitted) return;
    btn.disabled = busy;
    var spinner = form.querySelector('[data-submit-spinner]');
    var label = form.querySelector('[data-submit-label]');
    if (spinner) spinner.hidden = !busy;
    if (label) label.textContent = busy ? 'Submitting...' : 'Submit application';
  }

  /* v1.20.4: permanently disable the submit button after a successful
     submission so a maker cannot fire a duplicate application. One-way
     latch: form.__submitted also gates re-entry in submitForm and
     blocks setBusy from re-enabling the button. */
  function lockSubmit(form) {
    form.__submitted = true;
    var btn = form.querySelector('[data-submit]');
    if (!btn) return;
    btn.disabled = true;
    var spinner = form.querySelector('[data-submit-spinner]');
    var label = form.querySelector('[data-submit-label]');
    if (spinner) spinner.hidden = true;
    if (label) label.textContent = 'Application submitted';
  }

  /* v1.20.4: reveal the success popup, manage focus, bind close, and run
     the optional auto-redirect countdown. Any CTA click or a close
     cancels the countdown so the maker is never yanked away mid-read.
     Escape closes; Tab is trapped within the dialog. */
  function showSuccessModal(root) {
    var modal = root.querySelector('[data-success-modal]');
    if (!modal) return;
    modal.hidden = false;
    document.documentElement.classList.add('mr-modal-open');

    var seconds = parseInt(modal.getAttribute('data-redirect-seconds'), 10);
    if (!isFinite(seconds)) seconds = 0;
    /* v1.20.7: the auto-redirect target + its countdown label are now
       driven by data attributes (data-redirect-url / data-redirect-label)
       so the destination is configurable in the theme editor. Default is
       Etsy after 30s (the build-phase upsell). */
    var redirectUrl = modal.getAttribute('data-redirect-url') || '';
    var redirectLabel = modal.getAttribute('data-redirect-label') || 'the Maker Directory';
    var countdownEl = modal.querySelector('[data-success-countdown]');
    var timer = null;

    function cancelTimer() {
      if (timer) { clearInterval(timer); timer = null; }
      if (countdownEl) countdownEl.hidden = true;
    }
    function closeModal() {
      cancelTimer();
      modal.hidden = true;
      document.documentElement.classList.remove('mr-modal-open');
    }

    if (seconds > 0 && redirectUrl && countdownEl) {
      var remaining = seconds;
      countdownEl.hidden = false;
      countdownEl.textContent = 'Taking you to ' + redirectLabel + ' in ' + remaining + 's';
      timer = setInterval(function () {
        remaining -= 1;
        if (remaining <= 0) {
          clearInterval(timer);
          timer = null;
          window.location.href = redirectUrl;
          return;
        }
        countdownEl.textContent = 'Taking you to ' + redirectLabel + ' in ' + remaining + 's';
      }, 1000);
    }

    var closeBtn = modal.querySelector('[data-success-close]');
    var overlay = modal.querySelector('[data-success-overlay]');
    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    if (overlay) overlay.addEventListener('click', closeModal);
    $$(modal, '[data-success-cta], [data-success-directory]').forEach(function (a) {
      a.addEventListener('click', cancelTimer);
    });

    modal.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { closeModal(); return; }
      if (e.key !== 'Tab') return;
      var f = $$(modal, 'a[href], button:not([disabled])').filter(function (el) { return el.offsetParent !== null; });
      if (!f.length) return;
      var first = f[0];
      var last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    if (closeBtn && typeof closeBtn.focus === 'function') {
      try { closeBtn.focus(); } catch (e) {}
    }
  }

  /* ===== WIZARD ===== */
  function bindWizard(form) {
    if (!form.hasAttribute('data-wizard')) return;
    var steps = $$(form, '[data-wiz-step]');
    var root = form.closest('maker-register') || form;
    var fill = root.querySelector('[data-wiz-fill]');
    var text = root.querySelector('[data-wiz-text]');
    var cur = 0;

    /* Expose the wizard to submitForm so the Submit handler can route a
       validation failure back to the first step that contains an error,
       rather than silently failing while the user stares at step 3. */
    form.__wizard = {
      get current() { return cur; },
      steps: steps,
      gotoStep: function (i) {
        if (i < 0 || i >= steps.length) return;
        cur = i;
        render();
      },
    };

    function render() {
      steps.forEach(function (s, i) { s.classList.toggle('is-active', i === cur); });
      var pct = Math.round(((cur + 1) / steps.length) * 100);
      if (fill) fill.style.width = pct + '%';
      if (text) text.textContent = 'Step ' + (cur + 1) + ' of ' + steps.length;
      var top = root.getBoundingClientRect().top + window.scrollY - 16;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }
    /* Validate every named input/textarea/select in this step, not just the
       required ones, so format issues on optional fields (year, website,
       custom-text length) are caught before the user advances. */
    function validateStep() {
      var step = steps[cur];
      var ok = true;
      $$(step, 'input[name], textarea[name], select[name]').forEach(function (f) {
        /* Skip hidden inputs that are driven by the combobox helper. */
        if (f.type === 'hidden') return;
        /* Portfolio file input is driven by the custom file manager
           (`form.__portfolioFiles`), not the native input.files list.
           Validate it via the custom check below instead. */
        if (f.name === 'portfolio_images') return;
        if (!validateField(form, f)) ok = false;
      });
      $$(step, '[data-combo][data-required]').forEach(function (c) {
        var key = c.getAttribute('data-combo');
        var hidden = form.querySelector('[data-combo-value="' + key + '"]');
        var list = hidden && hidden.value ? JSON.parse(hidden.value) : [];
        var errEl = step.querySelector('[data-error="' + key + '"]');
        if (list.length === 0) {
          c.classList.add('is-invalid');
          var labelEl = c.parentElement && c.parentElement.querySelector('.maker-register__label');
          var labelText = labelEl ? (labelEl.textContent || '').replace(/\*$/, '').trim() : 'this field';
          if (errEl) errEl.textContent = 'Pick at least one option for ' + labelText.toLowerCase() + '.';
          ok = false;
        } else {
          c.classList.remove('is-invalid');
          if (errEl) errEl.textContent = '';
        }
      });
      /* Custom file-input validation. Portfolio files live in
         `form.__portfolioFiles` because the custom card lets the user
         add/remove individual images outside the native input.files
         list. */
      var portfolioInput = step.querySelector('[data-portfolio-input]');
      if (portfolioInput) {
        var portfolioErr = step.querySelector('[data-error="portfolio_images"]');
        var portfolioCard = step.querySelector('[data-portfolio-card]');
        var count = (form.__portfolioFiles || []).length;
        if (count === 0) {
          if (portfolioErr) portfolioErr.textContent = REQUIRED_FIELDS.portfolio_images;
          if (portfolioCard) portfolioCard.classList.add('is-invalid');
          ok = false;
        } else {
          if (portfolioErr) portfolioErr.textContent = '';
          if (portfolioCard) portfolioCard.classList.remove('is-invalid');
        }
      }
      return ok;
    }
    function scrollToFirstError(step) {
      var firstInvalid = step.querySelector('.is-invalid');
      if (!firstInvalid) return;
      firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (typeof firstInvalid.focus === 'function' && firstInvalid.tagName !== 'DIV') {
        try { firstInvalid.focus({ preventScroll: true }); } catch (e) {}
      }
    }
    $$(form, '[data-next]').forEach(function (b) {
      b.addEventListener('click', function () {
        if (!validateStep()) {
          scrollToFirstError(steps[cur]);
          showAlert(form, 'Please fix the highlighted fields before continuing.', 'error');
          return;
        }
        hideAlert(form);
        if (cur < steps.length - 1) { cur += 1; render(); }
      });
    });
    $$(form, '[data-prev]').forEach(function (b) {
      b.addEventListener('click', function () { if (cur > 0) { cur -= 1; render(); } });
    });
    render();
  }

  /* ===== SUBMIT ===== */
  function submitForm(root, form, apiUrl, successRedirect) {
    return new Promise(function (resolve) {
      /* v1.20.4: hard guard against re-submission after a successful
         application (covers Enter-key spam and any stray click that
         reaches the latched button). */
      if (form.__submitted) { resolve(false); return; }
      hideAlert(form);
      var ok = true;
      /* Validate EVERY named field in the form, not just required ones, so a
         format issue on an optional field (year, license length, etc.) does
         not slip through and fail server-side. */
      $$(form, 'input[name], textarea[name], select[name]').forEach(function (f) {
        if (f.type === 'hidden') return;
        /* Portfolio files live in form.__portfolioFiles (the native
           input.value is intentionally cleared after preprocessing in
           the change handler so the file picker is ready for more
           picks). The submit validator must read from the managed
           array, not from input.files. Without this skip, the native
           required check sees 0 files and falsely flags the field
           even when 10 are uploaded. */
        if (f.name === 'portfolio_images') return;
        if (!validateField(form, f)) ok = false;
      });
      /* Mirror the per-step custom portfolio validation here so submit
         actually verifies the managed array has at least one file. */
      var portfolioInput = form.querySelector('[data-portfolio-input]');
      if (portfolioInput) {
        var portfolioErr = form.querySelector('[data-error="portfolio_images"]');
        var portfolioCard = form.querySelector('[data-portfolio-card]');
        var portfolioCount = (form.__portfolioFiles || []).length;
        if (portfolioCount === 0) {
          if (portfolioErr) portfolioErr.textContent = REQUIRED_FIELDS.portfolio_images;
          if (portfolioCard) portfolioCard.classList.add('is-invalid');
          ok = false;
        } else {
          if (portfolioErr) portfolioErr.textContent = '';
          if (portfolioCard) portfolioCard.classList.remove('is-invalid');
        }
      }
      /* v1.20.8: mandatory safety-notice acknowledgment. The maker must
         tick the checkbox before the form submits. The server enforces
         it too (api/maker-registration.js), so this is UX, not the only
         gate. Marking the row .is-invalid lets the existing scroll-to-
         first-error + wizard-step routing pick it up. */
      var ackBox = form.querySelector('[name="security_acknowledgment"]');
      if (ackBox) {
        var ackRow = ackBox.closest('.maker-register__ack');
        var ackErr = form.querySelector('[data-error="security_acknowledgment"]');
        if (!ackBox.checked) {
          if (ackErr) ackErr.textContent = 'Please confirm you have read and understand the maker notice above.';
          if (ackRow) ackRow.classList.add('is-invalid');
          ok = false;
        } else {
          if (ackErr) ackErr.textContent = '';
          if (ackRow) ackRow.classList.remove('is-invalid');
        }
      }
      var requiredCombos = $$(form, '[data-combo][data-required]');
      for (var i = 0; i < requiredCombos.length; i++) {
        var c = requiredCombos[i];
        var key = c.getAttribute('data-combo');
        var hidden = form.querySelector('[data-combo-value="' + key + '"]');
        var list = hidden && hidden.value ? JSON.parse(hidden.value) : [];
        if (list.length === 0) {
          c.classList.add('is-invalid');
          var errEl = form.querySelector('[data-error="' + key + '"]');
          if (errEl) errEl.textContent = 'Pick at least one option.';
          ok = false;
        }
      }
      var preferred = form.querySelector('[data-preferred]');
      if (preferred && !preferred.value) {
        showAlert(form, 'Pick a preferred contact method.', 'error'); ok = false;
      }
      if (!ok) {
        /* Wizard-aware error routing: if the form is in wizard mode and the
           first invalid field lives on a different step, jump to that step so
           the user sees the error in context instead of staring at step 3. */
        var firstInvalid = form.querySelector('.is-invalid');
        if (firstInvalid && form.__wizard) {
          var wiz = form.__wizard;
          for (var s = 0; s < wiz.steps.length; s++) {
            if (wiz.steps[s].contains(firstInvalid)) {
              if (s !== wiz.current) wiz.gotoStep(s);
              break;
            }
          }
        }
        if (firstInvalid) setTimeout(function () {
          firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (typeof firstInvalid.focus === 'function' && firstInvalid.tagName !== 'DIV') {
            try { firstInvalid.focus({ preventScroll: true }); } catch (e) {}
          }
        }, 50);
        if (!form.querySelector('[data-alert]:not([hidden])')) showAlert(form, 'Please fix the highlighted fields and try again.', 'error');
        resolve(false); return;
      }
      setBusy(form, true);

      /* Direct-to-Shopify staged upload. File bytes go DIRECTLY from
         the browser to Shopify's S3 / GCS storage, never passing
         through our Vercel function. This sidesteps the function
         request-body limit entirely (the previous per-file endpoint
         still ate through that limit on large Canvas-encoded PNGs).
         Three small JSON round-trips per file (staged target -> S3
         POST -> register file), all sized in KB. */
      var stagedEndpoint = (apiUrl || '').replace(/\/maker-registration$/, '/staged-upload');
      var registerEndpoint = (apiUrl || '').replace(/\/maker-registration$/, '/register-file');

      function uploadOne(file, altText) {
        var resolvedAlt = altText || 'Maker image';
        /* v1.19.96: prefix every error thrown from this chain with the
           file name so the maker knows which photo failed (especially
           important when uploading multiple portfolio images at once).
           Server error messages are already humanized in v1.6.38; this
           just adds the file context as a "Problem with X:" prefix. */
        var fileLabel = file.name || 'your image';
        function wrap(msg) {
          return 'Problem with "' + fileLabel + '": ' + (msg || 'something went wrong. Please try a different photo.');
        }
        /* 1. Ask our function for a Shopify staged-upload target. */
        return fetch(stagedEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ filename: file.name, mimeType: file.type, fileSize: file.size }),
        }).then(function (r) {
          return r.json().catch(function () { return {}; }).then(function (p) {
            if (!r.ok) {
              throw new Error(wrap(p.error || 'we could not start the upload. Please refresh the page and try again.'));
            }
            return p;
          });
        }).then(function (target) {
          /* 2. POST file bytes DIRECTLY to Shopify's signed S3 URL. */
          var fd = new FormData();
          (target.parameters || []).forEach(function (param) { fd.append(param.name, param.value); });
          fd.append('file', file, file.name);
          return fetch(target.url, { method: 'POST', body: fd }).then(function (r) {
            if (!r.ok) {
              return r.text().catch(function () { return ''; }).then(function (txt) {
                /* Log the raw storage response so we can debug later; show
                   the user a friendly message that does not mention http
                   status codes. */
                console.error('[maker-register] storage upload failed', r.status, txt);
                throw new Error(wrap('the upload to our storage service did not complete. Please check your internet connection and try again.'));
              });
            }
            return target.resourceUrl;
          });
        }).then(function (resourceUrl) {
          /* 3. Register the staged file with Shopify, get the MediaImage GID. */
          return fetch(registerEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ resourceUrl: resourceUrl, altText: resolvedAlt, mimeType: file.type }),
          }).then(function (r) {
            return r.json().catch(function () { return {}; }).then(function (p) {
              if (!r.ok || !p.gid) {
                throw new Error(wrap(p.error || 'we could not save the image. Please try a different photo or refresh the page and try again.'));
              }
              return p.gid;
            });
          });
        }).catch(function (err) {
          /* v1.19.96: ensure bare network failures (offline, CORS, fetch
             rejection) also surface with file context. If the error was
             already wrapped by one of the steps above, pass it through
             unchanged; otherwise add the "Problem with X:" prefix here. */
          var m = (err && err.message) ? err.message : '';
          if (m.indexOf('Problem with "') === 0) throw err;
          throw new Error(wrap('the upload was interrupted. Please check your internet connection and try again.'));
        });
      }

      var logoFile = form.__logoFile || null;
      var portfolioFiles = (form.__portfolioFiles || []).slice();

      /* Build a maker-name slug from the company-name field so every file
         that lands on the Shopify CDN can be identified by name. The
         input is required + validated upstream; if it is somehow blank
         at submit time, renameUploadForCdn falls back to "maker". */
      var companyInput = form.querySelector('[name="company_name"]');
      var companySlug = slugifyForFilename(companyInput && companyInput.value);
      var companyLabel = (companyInput && companyInput.value && companyInput.value.trim()) || 'Maker';

      if (logoFile) {
        logoFile = renameUploadForCdn(logoFile, companySlug, 'logo');
      }
      portfolioFiles = portfolioFiles.map(function (f, i) {
        return renameUploadForCdn(f, companySlug, 'portfolio', i + 1);
      });

      Promise.resolve()
        .then(function () {
          /* Phase A: logo. */
          if (!logoFile) return null;
          return uploadOne(logoFile, 'Logo for ' + companyLabel);
        })
        .then(function (logoGid) {
          /* Phase A: portfolio (parallel). */
          return Promise.all(portfolioFiles.map(function (f, i) {
            return uploadOne(f, companyLabel + ' portfolio image ' + (i + 1));
          })).then(function (gids) {
            return { logoGid: logoGid, portfolioGids: gids };
          });
        })
        .then(function (gids) {
          /* Phase B: final submit with text + GIDs, no file bytes. */
          var fd = new FormData(form);
          /* Strip raw file fields; the server reads GIDs instead. */
          fd.delete('portfolio_images');
          fd.delete('image');
          if (gids.logoGid) fd.append('logo_gid', gids.logoGid);
          if (gids.portfolioGids && gids.portfolioGids.length > 0) {
            fd.append('portfolio_gids', JSON.stringify(gids.portfolioGids));
          }
          return fetch(apiUrl, { method: 'POST', body: fd }).then(function (res) {
            return res.json().catch(function () { return {}; }).then(function (payload) {
              if (!res.ok) {
                showAlert(form, payload.error || 'Something went wrong. Please try again.', 'error');
                setBusy(form, false);
                resolve(false);
                return;
              }
              if (successRedirect) { window.location.href = successRedirect; return; }
              /* v1.20.4: success popup replaces the inline alert. Lock the
                 submit button permanently (one-way latch) so the maker
                 cannot fire a duplicate application, reset the fields
                 behind the modal, then reveal the success popup with its
                 CTAs and optional auto-redirect countdown. */
              lockSubmit(form);
              form.reset();
              $$(form, '[data-combo-value]').forEach(function (h) { h.value = '[]'; });
              $$(form, '[data-combo]').forEach(function (c) { $$(c, '.maker-register__chip').forEach(function (x) { x.remove(); }); });
              form.__portfolioFiles = [];
              form.__logoFile = null;
              var counter = form.querySelector('[data-char-count]');
              if (counter) counter.textContent = '0 / 1500';
              bindLogo(form); bindPortfolio(form);
              showSuccessModal(root);
              resolve(true);
            });
          });
        })
        .catch(function (err) {
          console.error('[maker-register] submit failed', err);
          /* v1.19.96: messages from uploadOne are already wrapped with
             file context and humanized. For everything else (network
             total failure, JSON parse error, anything unexpected), fall
             back to a friendly generic line that does not mention
             technical details. */
          var msg = (err && err.message) ? err.message : '';
          if (!msg || msg.toLowerCase().indexOf('failed to fetch') !== -1 || msg.toLowerCase().indexOf('networkerror') !== -1) {
            msg = 'We could not reach our service. Please check your internet connection and try again.';
          }
          showAlert(form, msg, 'error');
          setBusy(form, false);
          resolve(false);
        });
    });
  }

  /* ===== DRAFT PERSISTENCE =====
     Save the user's progress to localStorage so a refresh restores
     every text/select/combo field + the current wizard step. Files
     (logo + portfolio) cannot be persisted across page loads (File
     objects are not serializable), so the user re-picks images. Text
     fields are the bulk of the work, so this still saves the maker
     from re-typing the whole story on an accidental refresh. Cleared
     on successful submit and after a 14 day idle window. */
  var DRAFT_STORAGE_KEY = 'kc-maker-register-draft-v1';
  var DRAFT_TTL_MS = 14 * 24 * 60 * 60 * 1000;

  function readDraft() {
    try {
      var raw = window.localStorage.getItem(DRAFT_STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || Date.now() - (parsed.savedAt || 0) > DRAFT_TTL_MS) {
        window.localStorage.removeItem(DRAFT_STORAGE_KEY);
        return null;
      }
      return parsed;
    } catch (e) { return null; }
  }

  function snapshotForm(form) {
    var fields = {};
    $$(form, 'input[name], textarea[name], select[name]').forEach(function (f) {
      /* File inputs are not serializable. */
      if (f.type === 'file') return;
      /* v1.20.8: never persist the safety acknowledgment in the draft.
         The maker should re-tick it each session so the recorded
         acknowledgment is a fresh, deliberate action. */
      if (f.hasAttribute('data-no-draft')) return;
      /* Skip the data-combo-value hiddens here; we capture them
         separately so we can JSON-decode them back into arrays. */
      if (f.hasAttribute('data-combo-value')) return;
      if (f.type === 'checkbox') {
        if (!fields[f.name]) fields[f.name] = [];
        if (f.checked) fields[f.name].push(f.value);
      } else if (f.type === 'radio') {
        if (f.checked) fields[f.name] = f.value;
      } else {
        fields[f.name] = f.value;
      }
    });
    var combos = {};
    $$(form, '[data-combo-value]').forEach(function (h) {
      try { combos[h.getAttribute('data-combo-value')] = JSON.parse(h.value || '[]'); } catch (e) {}
    });
    var step = (form.__wizard && typeof form.__wizard.current === 'number') ? form.__wizard.current : 0;
    return { fields: fields, combos: combos, step: step, savedAt: Date.now() };
  }

  function applyDraft(form, draft) {
    if (!draft) return;
    if (draft.fields) {
      Object.keys(draft.fields).forEach(function (name) {
        var value = draft.fields[name];
        var els = $$(form, '[name="' + name + '"]');
        els.forEach(function (el) {
          if (el.type === 'file') return;
          if (el.type === 'checkbox') {
            if (Array.isArray(value)) {
              el.checked = value.indexOf(el.value) !== -1;
            }
          } else if (el.type === 'radio') {
            el.checked = (el.value === value);
          } else {
            el.value = value == null ? '' : value;
          }
        });
      });
    }
    /* Combo hidden inputs must be set BEFORE bindPicker/bindCombobox
       runs so the chip render picks up the selections. init() already
       calls applyDraft prior to the bindPicker calls. */
    if (draft.combos) {
      Object.keys(draft.combos).forEach(function (key) {
        var hidden = form.querySelector('[data-combo-value="' + key + '"]');
        if (hidden && Array.isArray(draft.combos[key])) {
          hidden.value = JSON.stringify(draft.combos[key]);
        }
      });
    }
  }

  function bindDraftPersistence(form) {
    var saveTimer = null;
    function save() {
      try { window.localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(snapshotForm(form))); } catch (e) {}
    }
    function scheduleSave() {
      window.clearTimeout(saveTimer);
      saveTimer = window.setTimeout(save, 400);
    }
    form.addEventListener('input', scheduleSave);
    form.addEventListener('change', scheduleSave);
    /* Wizard step changes go through gotoStep but emit no event; wrap
       it so step transitions also persist. */
    if (form.__wizard && typeof form.__wizard.gotoStep === 'function') {
      var origGoto = form.__wizard.gotoStep.bind(form.__wizard);
      form.__wizard.gotoStep = function (i) {
        origGoto(i);
        save();
      };
    }
    form.__clearDraft = function () {
      try { window.localStorage.removeItem(DRAFT_STORAGE_KEY); } catch (e) {}
    };
  }

  function init(root) {
    var form = root.querySelector('[data-form]');
    if (!form) return;
    var apiUrl = root.dataset.apiUrl || '';
    var successRedirect = root.dataset.successRedirect || '';
    var config = readConfig(root);

    /* Restore the saved draft (if any) BEFORE the pickers bind so
       combo hidden inputs are already populated when bindPicker reads
       them to render selected chips. Step is restored AFTER bindWizard
       has wired up form.__wizard. */
    var draft = readDraft();
    if (draft) applyDraft(form, draft);

    bindPicker(form, 'states', (config.states || []).map(function (s) { return typeof s === 'string' ? { value: s, label: s } : s; }), 'Search states...');
    bindPicker(form, 'capabilities', config.capabilities || [], 'Search capabilities (laser, cnc, woodworking, etc.)...');
    bindPicker(form, 'project_types', config.project_types || [], 'Search project types (signs, furniture, leather, etc.)...');
    bindPicker(form, 'badges', config.badges || [], 'Search badges...');
    bindCharCount(form);
    bindPhone(form);
    bindWebsite(form);
    bindLogo(form);
    bindPortfolio(form);
    bindInlineValidation(form);
    bindStripAt(form);
    bindNoSpaces(form);
    bindPreferredContact(form);
    bindSafetyAck(form);
    bindWizard(form);

    if (draft && typeof draft.step === 'number' && form.__wizard) {
      form.__wizard.gotoStep(draft.step);
    }

    bindDraftPersistence(form);

    form.addEventListener('submit', function (ev) {
      ev.preventDefault();
      submitForm(root, form, apiUrl, successRedirect).then(function (ok) {
        if (ok && typeof form.__clearDraft === 'function') form.__clearDraft();
      });
    });
  }

  function bootstrap() { document.querySelectorAll('maker-register').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bootstrap);
  else bootstrap();
})();
